import { useId } from 'react';
import { Icone, type NomeDoIcone } from '../icones';
import './segmentado.css';

export type OpcaoSegmentada<T extends string> = {
  valor: T;
  rotulo: string;
  /**
   * Quantos itens esta opção tem, quando isso for sabido.
   *
   * Opcional porque quase nenhuma escolha tem contagem — período do Painel não
   * tem —, e um zero inventado ao lado de um rótulo diria que não há nada onde
   * ninguém contou.
   */
  contagem?: number;
  /** Um ícone antes do rótulo, nas abas que trocam de assunto (Automações). */
  icone?: NomeDoIcone;
};

export type SegmentadoProps<T extends string> = {
  /** Duas a quatro. Acima disso, é `CampoSelecao`. */
  opcoes: OpcaoSegmentada<T>[];
  escolhida: T;
  aoEscolher: (valor: T) => void;
  /** O que se está escolhendo, para o leitor de tela: "Período", "Situação". */
  rotulo: string;
  /**
   * `abas` troca o que a tela mostra — é `tablist`, e cada opção é `tab`.
   * `grupo` recorta o mesmo conteúdo — é um grupo de botões com `aria-pressed`,
   * como o período do Painel. Anunciar um filtro como aba faria o leitor de
   * tela prometer um painel que não existe.
   */
  como?: 'abas' | 'grupo';
  /**
   * `trilho` é o controle clássico: as opções dentro de uma calha, e a
   * escolhida sobe para a superfície. `pilulas` é o desenho novo (v3): cada
   * opção é uma pílula solta, e a escolhida fica roxa cheia — as abas da
   * lista de conversas. `sublinhado` são as abas de texto com o traço roxo
   * embaixo da escolhida — o painel do cliente.
   */
  aparencia?: 'trilho' | 'pilulas' | 'sublinhado';
  /**
   * Prefixo dos ids das abas, para quem monta `tabpanel`s: cada aba sai com
   * `id="<prefixo>-<valor>"` e `aria-controls="<prefixo>-painel-<valor>"`.
   * Sem prefixo, gera um. Só vale para `como='abas'`.
   */
  idDasAbas?: string;
};

/** O id do painel que a aba `valor` controla, para o `tabpanel` usar. */
export function idDoPainel(prefixo: string, valor: string): string {
  return `${prefixo}-painel-${valor}`;
}

export function Segmentado<T extends string>({
  opcoes,
  escolhida,
  aoEscolher,
  rotulo,
  como = 'abas',
  aparencia = 'trilho',
  idDasAbas,
}: SegmentadoProps<T>) {
  const abas = como === 'abas';
  const gerado = useId();
  const prefixo = idDasAbas ?? gerado;
  const classe =
    aparencia === 'pilulas'
      ? 'segmentado segmentado--pilulas'
      : aparencia === 'sublinhado'
        ? 'segmentado segmentado--sublinhado'
        : 'segmentado';
  return (
    <div className={classe} role={abas ? 'tablist' : 'group'} aria-label={rotulo}>
      {opcoes.map((opcao) => (
        <button
          key={opcao.valor}
          type="button"
          id={abas ? `${prefixo}-${opcao.valor}` : undefined}
          role={abas ? 'tab' : undefined}
          className="segmentado__opcao"
          aria-selected={abas ? opcao.valor === escolhida : undefined}
          // Só quando quem monta a tela DÁ o prefixo: é a promessa de que o
          // `tabpanel` com esse id existe. Apontar para um id que não existe
          // é o que o axe acusa, e a maioria das abas do produto não tem painel.
          aria-controls={abas && idDasAbas ? idDoPainel(prefixo, opcao.valor) : undefined}
          aria-pressed={abas ? undefined : opcao.valor === escolhida}
          onClick={() => aoEscolher(opcao.valor)}
        >
          {opcao.icone && <Icone nome={opcao.icone} />}
          {opcao.rotulo}
          {opcao.contagem !== undefined && (
            /*
             * Dentro do botão, e não ao lado: o número é parte do nome
             * acessível da opção — "Minhas 18" é o que o leitor de tela
             * anuncia, e é o que está escrito.
             */
            <span className="segmentado__contagem">{opcao.contagem}</span>
          )}
        </button>
      ))}
    </div>
  );
}
